<script setup lang="ts">
import StoryCard from '@/components/customer-stories/StoryCard.vue'
import { getVideoObjectSchema, stories } from '@/data/stories'
import { useHead } from '@unhead/vue'
import { easeOut, motion } from 'motion-v'

const { t } = useI18n()

const videoSchemas = stories.map(getVideoObjectSchema).filter(Boolean)

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${import.meta.env.VITE_MONOLITH_URL}/reviews`,
    },
  ],
  script: videoSchemas.map(schema => ({
    type: 'application/ld+json',
    innerHTML: JSON.stringify(schema),
  })),
})

useSeoMeta({
  title: t('customer_reviews.meta.title'),
  description: t('customer_reviews.meta.description'),
  ogTitle: t('customer_reviews.meta.title'),
  ogDescription: t('customer_reviews.meta.description'),
})

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const storyVariants = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: easeOut } },
}

const buttonVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: easeOut } },
}
</script>

<template>
  <div class="isolate">
    <LayoutNavbar />

    <div class="mb-0 bg-[#f8f8f0] pt-10 md:shadow-2xl md:shadow-black/10">
      <!-- Headline -->
      <section class="3xl:px-0 mx-auto mb-8 flex flex-col items-center px-4 lg:max-w-screen-xl">
        <motion.h1
          class="block text-balance text-center text-3xl font-medium !leading-tight text-slate-900 lg:text-5xl"
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
        >
          Rated 4.8/5 on G2 from 100+ reviews
        </motion.h1>
        <motion.p
          class="text-balance py-6 text-center text-xl text-slate-700"
          :initial="{ opacity: 0, y: 20 }"
          :while-in-view="{ opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.4 } }"
        >
          Restaurant operators trust SpotHopper for easier marketing, stronger online presence,<br class="hidden lg:block"> and real business growth — see what verified users are saying.
        </motion.p>
        <motion.div
          :variants="buttonVariants"
          initial="initial"
          while-in-view="animate"
        >
          <UiButton as-child>
            <a
              href="https://www.g2.com/products/spothopper/reviews"
              target="_blank"
              rel="noopener noreferrer"
            >
              View our G2 reviews
            </a>
          </UiButton>
        </motion.div>
      </section>

      <!-- Categories -->
      <div class="mb-8 w-full bg-white pb-8 pt-8 md:rounded-[5rem]">
        <div class="mx-auto w-[90%] pb-8 pt-0 text-center">
          <h2 class="leading-1 lg:text-4xl text-2xl font-medium text-slate-900">
            Straight From the Source
          </h2>
          <p class="mt-4 text-lg text-slate-700">
            Explore selected customer stories and performance highlights from operators using SpotHopper.
          </p>
        </div>
        <section class="mx-auto mb-8 px-2 lg:max-w-screen-xl xl:px-0">
          <div class="flex w-full flex-col gap-y-6 px-4 xl:px-0">
            <motion.div
              v-for="(story, index) in stories"
              :key="story.slug"
              :variants="storyVariants"
              initial="initial"
              while-in-view="animate"
              :in-view-options="{ once: true, margin: '0px 0px -10% 0px' }"
            >
              <StoryCard
                v-bind="story"
                :index="index"
              />
            </motion.div>
          </div>
        </section>
        <section class="mx-auto px-4 pt-4 md:px-6 lg:max-w-screen-xl xl:px-0 2xl:px-0">
          <p class="text-right text-sm italic text-slate-500">
            *Results shown are based on selected customer performance data from April 2026. MoM comparisons reflect April 2026 vs. March 2026; YoY comparisons reflect April 2026 vs. April 2025.
          </p>
        </section>

        <!-- CTA Section -->
        <section class="mx-auto px-4 md:px-6 xl:px-0 pb-10 lg:max-w-screen-xl lg:pb-16 2xl:px-0 pt-[50px]">
          <div class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20">
            <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
              <img
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              >
            </div>

            <div class="isolate grid place-items-center gap-5">
              <h3 class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl">
                {{ t('home.cta_banner.title') }}
              </h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  {{ t('home.cta_banner.action') }}

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
